import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatIdentifiers } from "@/lib/format";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ReportGroup from "@/components/admin/ReportGroup";
import ReportActionDialog, { ACTIONS } from "@/components/admin/ReportActionDialog";

function describe(action, res) {
  const parts = [`${ACTIONS[action].label}: resolved ${res.resolved} report${res.resolved === 1 ? "" : "s"}`];
  if (res.removed) parts.push(`removed ${res.removed.length} photo${res.removed.length === 1 ? "" : "s"}${res.below_minimum ? " (now below the photo minimum, hidden)" : ""}`);
  if (action === "hide") parts.push("profile hidden from Discover");
  if (action === "warn") parts.push("warning sent");
  if (res.ban_case_id) parts.push(`ban case ${res.ban_case_id}`);
  if (res.banned_identifiers) parts.push(`blocked: ${formatIdentifiers(res.banned_identifiers)}`);
  return parts.join(" · ");
}

export default function Reports() {
  const [status, setStatus] = useState("open");
  const { data, error, loading, reload } = useAdminLoad(`/admin/reports?status=${status}`);
  const [pending, setPending] = useState(null);
  const [result, setResult] = useState(null);
  const items = data?.items || [];

  const onDone = (action, res) => {
    setResult({ name: pending?.group.reported.first_name, text: describe(action, res) });
    toast.success(`${ACTIONS[action].label} done.`);
    reload();
  };

  return (
    <div data-testid="admin-reports-page">
      <PageHeader title="Reports" description={`Grouped by person. Underage first, then auto-hidden profiles (review within ${data?.review_hours ?? 24} hours), then the rest, oldest first.`} />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>{["open", "resolved"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`reports-tab-${s}`}>{s}</TabsTrigger>)}</TabsList>
        </Tabs>
        {data && !data.can_act && <Pill data-testid="reports-readonly-note">Read-only for your role</Pill>}
      </div>
      {result && (
        <div className="cw-tone-success mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" role="status" data-testid="report-action-result">
          <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" /><p><strong className="font-semibold">{result.name}</strong> · {result.text}</p>
        </div>
      )}
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <div className="space-y-4">
          {items.map((g) => <ReportGroup key={g.reported.id} g={g} open={status === "open"} canAct={!!data?.can_act} onAction={(grp, action) => setPending({ group: grp, action })} />)}
          {!items.length && <Panel><Empty title={status === "open" ? "No open reports" : "No resolved reports"} text="Reports appear here as people send them." testId="reports-empty" /></Panel>}
        </div>
      </LoadState>
      <ReportActionDialog action={pending?.action} group={pending?.group} onOpenChange={(o) => !o && setPending(null)} onDone={onDone} />
    </div>
  );
}
